import { createRoot, Root } from "react-dom/client";
import { flushSync } from "react-dom";
import { MemoryRouter } from "react-router-dom";
import { toJpeg } from "html-to-image";
import { jsPDF } from "jspdf";
import { ReactElement } from "react";
import { I18nextProvider } from "react-i18next";
import { MotionConfig } from "framer-motion";
import i18n from "i18n";
import {
  A4_HEIGHT_MM,
  A4_HEIGHT_PX,
  A4_WIDTH_MM,
  A4_WIDTH_PX,
  PDF_CAPTURE,
  PDF_COLORS,
  PDF_FONT_FAMILIES,
} from "./pdfConstants";
import { ensureFontsReady, injectFontFaces } from "./pdfFonts";

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function yieldToMain(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof (window as any).scheduler?.yield === "function") {
      (window as any).scheduler.yield().then(resolve);
      return;
    }
    setTimeout(resolve, 0);
  });
}

/** Disable Chart.js animations globally so pies/radars render fully. */
function disableChartAnimations(): void {
  const g: any = window as any;
  const Chart = g.Chart;
  if (Chart?.defaults) {
    Chart.defaults.animation = false;
    Chart.defaults.animations = false;
    if ("transitions" in Chart.defaults) Chart.defaults.transitions = {};
  }
}

/** Wait until every <img> inside the page is loaded/decoded. */
async function waitForImages(page: HTMLElement): Promise<void> {
  await Promise.all(
    Array.from(page.querySelectorAll("img")).map((img) =>
      img.complete && img.naturalWidth > 0
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            img.onload = () => resolve();
            img.onerror = () => resolve();
          })
    )
  );
}

function createHost(): HTMLDivElement {
  const host = document.createElement("div");
  host.setAttribute("data-pdf-host", "true");
  host.style.cssText = [
    "position:fixed",
    "left:-10000px",
    "top:0",
    `width:${A4_WIDTH_PX}px`,
    `height:${A4_HEIGHT_PX}px`,
    "overflow:hidden",
    "pointer-events:none",
    "z-index:-1",
    "opacity:1",
    "background:#fff",
  ].join(";");
  document.body.appendChild(host);
  injectFontFaces(host);
  return host;
}

async function capturePage(
  host: HTMLDivElement,
  root: Root,
  element: ReactElement,
  settleMs: number
): Promise<string> {
  await ensureFontsReady(PDF_FONT_FAMILIES);
  disableChartAnimations();

  // Snapshot the current path so useScope()/useLocation() work inside PDF
  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : "/";

  flushSync(() => {
    root.render(
      <MemoryRouter initialEntries={[currentPath]}>
        <I18nextProvider i18n={i18n}>
          <MotionConfig reducedMotion="always">{element}</MotionConfig>
        </I18nextProvider>
      </MemoryRouter>
    );
  });

  await wait(settleMs);
  await yieldToMain();
  await ensureFontsReady(PDF_FONT_FAMILIES);
  await wait(200);

  const page = host.querySelector("[data-pdf-page]") as HTMLElement | null;
  if (!page) throw new Error("PDF page element not found");

  await waitForImages(page);

  const dataUrl = await toJpeg(page, {
    quality: PDF_CAPTURE.jpegQuality,
    backgroundColor: PDF_COLORS.paper,
    width: A4_WIDTH_PX,
    height: A4_HEIGHT_PX,
    pixelRatio: PDF_CAPTURE.scale,
    cacheBust: false,
    skipFonts: false,
  });

  return dataUrl;
}

export interface GeneratePdfOptions {
  fileName: string;
  pages: ReactElement[];
  onProgress?: (current: number, total: number) => void;
  settleMs?: number;
}

export async function generateAssessmentPdf({
  fileName,
  pages,
  onProgress,
  settleMs = PDF_CAPTURE.settleMs,
}: GeneratePdfOptions): Promise<void> {
  if (!pages.length) return;

  await ensureFontsReady(PDF_FONT_FAMILIES);

  const host = createHost();
  const root = createRoot(host);
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  try {
    for (let i = 0; i < pages.length; i++) {
      onProgress?.(i + 1, pages.length);
      await yieldToMain();

      const dataUrl = await capturePage(host, root, pages[i], settleMs);

      if (i > 0) pdf.addPage();
      pdf.addImage(
        dataUrl,
        "JPEG",
        0,
        0,
        A4_WIDTH_MM,
        A4_HEIGHT_MM,
        undefined,
        "FAST"
      );

      await yieldToMain();
    }

    pdf.save(fileName);
  } finally {
    flushSync(() => root.render(null));
    root.unmount();
    host.remove();
  }
}
